// src/features/tasks/components/TaskListPage/TasksDataGrid/index.jsx
import React, { useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';

// Components
import CustomToolbar from './components/CustomToolbar';
import TasksEmptyState from '../TasksEmptyState';

// Hooks
import { useTasksDataGrid } from './hooks/useTasksDataGrid';

// Constants and utils
import { createColumns } from './constants/columnDefinitions';
import { dataGridStyles } from './utils/dataGridUtils';

// Empty state overlay component
function NoRowsOverlay({ currentFilter, canCreate, onCreateClick }) {
  return (
    <TasksEmptyState
      currentFilter={currentFilter}
      canCreate={canCreate}
      onCreateClick={onCreateClick}
    />
  );
}

// Grouped by state (overdue, in progress, pending, ...), then by due date
const DEFAULT_SORT_MODEL = [
  { field: 'sort_priority', sort: 'asc' },
  { field: 'due_date', sort: 'asc' },
];

export default function TasksDataGrid({
  tasks,
  loading,
  canUpdate,
  canDelete,
  canChangeState,
  onView,
  onEdit,
  onDelete,
  onStateChange,
  currentFilter,
  canCreate,
  onCreateClick,
  sortModel = DEFAULT_SORT_MODEL,
}) {
  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    tasksWithSortFields,
    getTaskSortPriority,
    getRowClassName,
  } = useTasksDataGrid(tasks);

  // Define columns
  const columns = useMemo(() => 
    createColumns({
      canUpdate,
      canDelete,
      canChangeState,
      onView,
      onEdit,
      onDelete,
      onStateChange,
      getTaskSortPriority,
    }), 
    [canUpdate, canDelete, canChangeState, onView, onEdit, onDelete, onStateChange, getTaskSortPriority]
  );

  // Row click handler
  const handleRowClick = useCallback((params) => {
    onView(params.row);
  }, [onView]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      <DataGrid
        rows={tasksWithSortFields}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.task_id}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          sorting: {
            sortModel,
          },
          columns: {
            columnVisibilityModel: {
              sort_priority: false,
            },
          },
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <NoRowsOverlay
              currentFilter={currentFilter}
              canCreate={canCreate}
              onCreateClick={onCreateClick}
            />
          ),
        }}
        sx={dataGridStyles}
      />
    </Box>
  );
}